import { useEffect, useState } from 'react';
import { useVoiceHelp } from '../../contexts/VoiceHelpContext';
import { useAgentBeta } from '../../contexts/AgentBetaContext';
import { supabase } from '../../lib/supabase';
import NexusFace from '../NexusFace';
import BotAvatar, { avatarGlyph } from './BotAvatar';
import BotVoiceBar from './BotVoiceBar';
import { updateBot } from './api';
import { agentVoice } from './voices';
import type { BotRow } from './types';

/**
 * A bot call that carries on outside its chat: the same pill the chat shows,
 * floated under the header on whatever screen the owner is on. Press-and-hold
 * on the AI button starts one with Nexus without leaving the page; tapping the
 * face, "Type", or the AI button opens the conversation it belongs to.
 */
export default function FloatingVoicePill() {
  const {
    status,
    surface,
    chatTarget,
    chatBarHosted,
    micStream,
    remoteStream,
    micMuted,
    toggleMicMuted,
    stopVoice,
    applyVoiceChange,
  } = useVoiceHelp();
  const { openAgents } = useAgentBeta();
  const live = surface === 'chat' && (status === 'active' || status === 'connecting');
  const botId = live ? chatTarget?.botId || null : null;
  const [bot, setBot] = useState<BotRow | null>(null);

  useEffect(() => {
    if (!botId) { setBot(null); return; }
    if (bot?.id === botId) return;
    let cancelled = false;
    void supabase.from('agent_bots').select('*').eq('id', botId).maybeSingle()
      .then(({ data }) => { if (!cancelled && data) setBot(data as BotRow); });
    return () => { cancelled = true; };
  }, [botId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!live || chatBarHosted || !chatTarget) return null;

  const openChat = () => openAgents(chatTarget.chatId ? `/agents?chat=${chatTarget.chatId}` : '/agents');
  const saveVoice = async (voice: string) => {
    if (!bot) return;
    const next = agentVoice(voice);
    if (agentVoice(bot.voice) === next) return;
    setBot({ ...bot, voice: next });
    try {
      await updateBot(bot.id, { voice: next });
      applyVoiceChange();
    } catch { /* the pill keeps the old voice on the next load */ }
  };
  const face = bot && !bot.is_master
    ? <BotAvatar glyph={avatarGlyph(bot)} colour={bot.colour} size={36} />
    : <NexusFace size={36} title={bot?.name || 'Nexus'} />;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-[95] flex justify-center px-3"
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 3.75rem)' }}
      data-floating-voice-pill
    >
      <BotVoiceBar
        micStream={micStream}
        remoteStream={remoteStream}
        connecting={status === 'connecting'}
        muted={micMuted}
        voice={agentVoice(bot?.voice)}
        face={face}
        onVoice={(voice) => { void saveVoice(voice); }}
        onType={openChat}
        onOpen={openChat}
        onMute={toggleMicMuted}
        onEnd={stopVoice}
      />
    </div>
  );
}
